import {z as zod} from "zod";
import {PaymentCurrency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentCurrency";
import {PaymentFrequency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentFrequency";
import {PaymentGateway} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentGateway";
import {PaymentMode} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentMode";
import {PaymentType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentType";
import type {Integer, Iso8601Date, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    integerZodValidator,
    iso8601DateTimeZodValidator,
    iso8601DateZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";

export enum PaymentSessionPurpose {
    continueExistingPlan = "f8c0b1d2-3e4f-4a5c-9b6d-7c8e1f2b3a4b",
    startNewPlan = "c525eb82-e5fe-4fd9-87d4-32761b09e1c5",
}

export enum PaymentStatus {
    failed = "0323076f-73aa-410b-93a5-0191b7de63b1",
    pending = "09513f62-df9c-4dca-b867-eceed319bb64",
    succeeded = "c77afff1-cb81-4ad0-8ef2-5086050b6009",
}

export type PaymentSessionPaymentDetails = {
    initialAmount: Integer;
    isFirstPayment: boolean;
    paymentCurrency: PaymentCurrency;
    paymentFrequency: PaymentFrequency;
    paymentGateway: PaymentGateway;
    paymentGatewayResponse: Record<string, unknown> | null;
    paymentMode: PaymentMode | null;
    paymentType: PaymentType.recurring;
    recurringAmount: Integer;
    recurringEndDate: Iso8601Date;
    recurringStartDate: Iso8601Date;
    status: PaymentStatus;
};

export type PaymentSessionPurposeDetails = {
    planId: Uuid;
    purpose: PaymentSessionPurpose;
    workspaceId: Uuid;
};

export type PaymentSession = {
    createdAt: Iso8601DateTime;
    id: Uuid;
    paymentDetails: PaymentSessionPaymentDetails;
    purposeDetails: PaymentSessionPurposeDetails;
    updatedAt: Iso8601DateTime;
};

export type PayuWebhookPayload = {
    amount: string;
    email: string;
    firstname: string;
    hash: string;
    productinfo: string;
    status: string;
    txnid: string;
    udf1: string | null;
    udf2: string | null;
    udf3: string | null;
    udf4: string | null;
    udf5: string | null;
};

export const paymentSessionPaymentDetailsZodValidator = zod.object({
    initialAmount: integerZodValidator,
    isFirstPayment: zod.boolean(),
    paymentCurrency: zod.enum(PaymentCurrency),
    paymentFrequency: zod.enum(PaymentFrequency),
    paymentGateway: zod.enum(PaymentGateway),
    paymentGatewayResponse: zod.record(zod.string(), zod.unknown()).nullable(),
    paymentMode: zod.enum(PaymentMode).nullable(),
    paymentType: zod.literal(PaymentType.recurring),
    recurringAmount: integerZodValidator,
    recurringEndDate: iso8601DateZodValidator,
    recurringStartDate: iso8601DateZodValidator,
    status: zod.enum(PaymentStatus),
});

export const paymentSessionPurposeDetailsZodValidator = zod.object({
    planId: uuidZodValidator,
    purpose: zod.enum(PaymentSessionPurpose),
    workspaceId: uuidZodValidator,
});

export const paymentSessionZodValidator = zod.object({
    createdAt: iso8601DateTimeZodValidator,
    id: uuidZodValidator,
    paymentDetails: paymentSessionPaymentDetailsZodValidator,
    purposeDetails: paymentSessionPurposeDetailsZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
});

export const payuWebhookPayloadZodValidator = zod.object({
    amount: zod.union([zod.number(), zod.string()]).transform((value) => String(value)),
    email: zod.string(),
    firstname: zod.string(),
    hash: zod.string(),
    productinfo: zod.string(),
    status: zod.string(),
    txnid: zod.string(),
    udf1: zod.string().nullable(),
    udf2: zod.string().nullable(),
    udf3: zod.string().nullable(),
    udf4: zod.string().nullable(),
    udf5: zod.string().nullable(),
});
